"use client";

import { useUniversalAuth } from "@wtc/ui";
import Link from "next/link";
import Image from "next/image";
import { EnhancedNavigation } from "./navigation/enhanced-navigation";
import { EnhancedQuickActions } from "./navigation/enhanced-quick-actions";
import { MobileBottomDrawer } from "./navigation/mobile-bottom-drawer";
import { usePathname } from "next/navigation";
import { debug } from "@/lib/utils/debug";
import { useMemo } from "react";
import { buildNavigationSections } from "./navigation/navigation-builder";
import { useNavigationCustomization } from "@/lib/hooks/settings/use-navigation-customization";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { useFeatureFlags } from "@/lib/contexts/feature-flags-context";
import { CLIENT_NAV_ITEMS } from "./navigation/client-navigation";
import { Menu } from "lucide-react";
import { Button } from "./button";

export function Header() {
  const { appUser, isAuthenticated, isLoading, isClientUser } = useUniversalAuth();
  const pathname = usePathname();
  const { isFeatureEnabled } = useFeatureFlags();
  const roleCheck = useRoleCheck();

  // Memoize default navigation sections for mobile drawer
  const defaultSections = useMemo(() => {
    const config = {
      userType: roleCheck.roleName || "",
      isAdmin: roleCheck.isAdmin,
      userEmail: appUser?.email,
      sensitiveFeatureAccess: appUser?.sensitive_feature_access,
      sections: [],
      featureFlags: {
        canViewDashboard: isFeatureEnabled("canViewDashboard"),
        canViewTickets: isFeatureEnabled("canViewTickets"),
        canViewInternalTickets: isFeatureEnabled("canViewInternalTickets"),
        canCreateTickets: isFeatureEnabled("canCreateTickets"),
        canUseTicketTemplates: isFeatureEnabled("canUseTicketTemplates"),
        showTimeTrackingMenu: isFeatureEnabled("showTimeTrackingMenu"),
        canViewAllTimeEntries: isFeatureEnabled("canViewAllTimeEntries"),
        canCreateTimeEntries: isFeatureEnabled("canCreateTimeEntries"),
        canViewReports: isFeatureEnabled("canViewReports"),
        showClientMenu: isFeatureEnabled("showClientMenu"),
        canViewClients: isFeatureEnabled("canViewClients"),
        canCreateClients: isFeatureEnabled("canCreateClients"),
        showAdminMenu: isFeatureEnabled("showAdminMenu"),
        canViewUsers: isFeatureEnabled("canViewUsers"),
        canManageAutomation: isFeatureEnabled("canManageAutomation"),
        canManageData: isFeatureEnabled("canManageData"),
        canManageSystemSettings: isFeatureEnabled("canManageSystemSettings"),
        canViewTasks: isFeatureEnabled("canViewTasks"),
        canCreateTasks: isFeatureEnabled("canCreateTasks"),
        canEditTasks: isFeatureEnabled("canEditTasks"),
        canDeleteTasks: isFeatureEnabled("canDeleteTasks"),
        canAssignTasks: isFeatureEnabled("canAssignTasks"),
        canChangeTaskStatus: isFeatureEnabled("canChangeTaskStatus"),
        canChangeTaskPriority: isFeatureEnabled("canChangeTaskPriority"),
        canViewAllTasks: isFeatureEnabled("canViewAllTasks"),
        canViewMyTasks: isFeatureEnabled("canViewMyTasks"),
        canExportTasks: isFeatureEnabled("canExportTasks"),
      },
    };
    return buildNavigationSections(config);
  }, [isFeatureEnabled, roleCheck.isAdmin, roleCheck.roleName, appUser?.email, appUser?.sensitive_feature_access]);

  const { visibleItems, allItems, isLoaded } = useNavigationCustomization(defaultSections);

  // Don't render header on home page or if user is not authenticated or still loading
  if (pathname === "/" || pathname === "/for-msps" || !isAuthenticated || isLoading) {
    debug.log("Header: Not rendering", {
      pathname,
      isAuthenticated,
      isLoading,
    });
    return null;
  }

  // Don't render until customization is loaded for employee/admin views
  if (!isLoaded && !isClientUser) {
    return null;
  }

  debug.log("Header: Rendering", {
    pathname,
    isAuthenticated,
    userType: appUser?.role,
  });

  return (
    <header className="bg-card border-b border-border shadow-sm">
      {/* Desktop Header - Single bar with enhanced design */}
      <div className="hidden sm:block">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex items-center justify-between h-11">
            {/* Left: Logo + Main Navigation */}
            <div className="flex items-center space-x-4">
              {/* Main Navigation - Hidden on smaller desktop screens, shown on large */}
              <nav className="hidden lg:flex items-center space-x-4">
                <Link href="/dashboard" className="flex items-center group">
                  <Image
                    src="/logo_long.svg"
                    alt="West Tennessee Consulting Hub"
                    width={170}
                    height={40}
                    className="h-9 w-auto group-hover:opacity-80 transition-opacity mr-4"
                  />
                </Link>
                <EnhancedNavigation />
              </nav>

              {/* Logo only for medium screens when nav is hidden */}
              <Link href="/dashboard" className="flex items-center group lg:hidden">
                <Image
                  src="/logo_long.svg"
                  alt="West Tennessee Consulting Hub"
                  width={188}
                  height={44}
                  className="h-10 w-auto group-hover:opacity-80 transition-opacity"
                />
              </Link>
            </div>

            {/* Right: Quick Actions */}
            <div className="flex items-center space-x-2">
              <EnhancedQuickActions />

              {/* Navigation menu for tablet widths (640px–1023px) where the full nav is hidden.
                  Without this, the 640–1023 range (iPad portrait + landscape) had no nav surface. */}
              <div className="lg:hidden">
                <MobileBottomDrawer
                  items={isClientUser ? CLIENT_NAV_ITEMS : allItems}
                  pathname={pathname || "/"}
                  tooltipText="Open navigation menu"
                  trigger={
                    <Button variant="ghost" size="sm" className="p-2" aria-label="Open navigation menu">
                      <Menu className="h-5 w-5" />
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header - Simplified and clean */}
      <div className="sm:hidden">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo - Simplified */}
            <Link href="/dashboard" className="flex items-center">
              <Image src="/logo.svg" alt="WTC Hub" width={120} height={32} className="h-8 w-auto" />
            </Link>

            {/* Mobile Navigation Trigger */}
            <div className="flex items-center">
              <MobileBottomDrawer
                items={isClientUser ? CLIENT_NAV_ITEMS : allItems}
                pathname={pathname || "/"}
                tooltipText="Open navigation menu"
                trigger={
                  <Button variant="ghost" size="sm" className="p-2" aria-label="Open navigation menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
